import React, { FunctionComponent, useMemo } from 'react';

import click from '@gojob/tracking/click';
import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';

import { useLocation } from '@reach/router';
import slugify from 'slugify';

import useTranslation from '../../translations/useTranslation';
import BreakpointContainer from '../BreakpointContainer';

export type TableOfContentsProps = {
  blocks?: {
    _key?: string;
    _type?: string;
    style?: string;
    children?: {
      _type?: string;
      text?: string;
    }[];
  }[];
};

type LinkProps = {
  className?: string;
  children?: string;
};
const Link: FunctionComponent<LinkProps> = (props) => {
  const { children = '', className } = props;
  const href = `#${slugify(children)}`;
  const { pathname } = useLocation();
  const trackClick = () => {
    click({
      eventAction: 'click-anchor',
      // remove trailing '/' if present
      eventCategory: pathname.replace(/(.)(\/)$/, '$1'),
      eventName: slugify(children),
    });
  };

  return (
    <li className={className}>
      <Text variant="link" data-testid="Link" as="a" href={href} onClick={trackClick}>
        {children}
      </Text>
    </li>
  );
};

const useStyles = () => {
  const { colors, spacing } = useTheme();
  return {
    root: {
      backgroundColor: colors.lightBackground,
      boxShadow: `inset 0 0 6px 0 rgba(35,44,122,0.26)`,
      width: '100%',
      margin: spacing(4, 0),
    },
    list: {
      margin: spacing(-0.5, 0),
      padding: spacing(0, 0, 0, 4),
      color: colors.primary,
    },
    listItem: {
      margin: spacing(3, 0),
    },
  };
};

const TableOfContents: FunctionComponent<TableOfContentsProps> = (props) => {
  const blocks = props.blocks || [];

  const t = useTranslation();
  const classes = useStyles();

  const anchors = useMemo(() => {
    return blocks.filter((block = {}) => {
      const { _type, style, children } = block;
      return _type === 'block' && style === 'h2' && children?.length === 1 && children?.[0].text;
    });
  }, [blocks]);

  if (!anchors.length) {
    return null;
  }

  return (
    <Flex css={classes.root} margin={[0, -4]} padding={4} flexDirection="column" alignItems="center">
      <BreakpointContainer flexDirection="column" maxWidth="lg">
        <Text variant="headline4">{t('table_of_contents')}</Text>

        <ul css={classes.list}>
          {anchors.map((anchor, index) => {
            return (
              <Link css={classes.listItem} key={anchor?._key || index}>
                {anchor?.children?.[0].text}
              </Link>
            );
          })}
        </ul>
      </BreakpointContainer>
    </Flex>
  );
};

export default TableOfContents;
